import type { FlightAlertRow } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { formatHhMm, isSupportedTimeZone } from '@web/ui/core/utils';

// A scheduled-vs-estimated gap under this is noise, not a change worth
// striking the scheduled time through for.
export const STRIKETHROUGH_THRESHOLD_MIN = 5;

// The operator's zone, resolved once per module rather than per cell per
// render (this table renders up to 300 rows). The fallback for a row whose
// departure zone is unknown to cirium's table or to this browser's ICU.
const LOCAL_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The zone a cell formats in, and whether it is the airport's. The server's
 *  zone wins when there is one this browser can format in; otherwise the
 *  operator's own, and the title says so rather than claiming a zone the cell
 *  did not use. Named for the departure, used for the arrival too. */
export function departureZone(timeZone: string | null): { zone: string; isAirport: boolean } {
  if (timeZone !== null && isSupportedTimeZone(timeZone))
    return { zone: timeZone, isAirport: true };
  return { zone: LOCAL_ZONE, isAirport: false };
}

/** `name`, or the masked `customerId` (last 3 digits) when the trip carries no
 *  name. `||`, not `??`: customer-api normalizes a missing name to `''`, which
 *  would otherwise render an empty cell. */
export function travelerDisplayName(row: FlightAlertRow): string {
  return row.traveler.name?.trim() || `…${row.traveler.customerId.slice(-3)}`;
}

export function noticeText(row: FlightAlertRow): string {
  switch (row.notice.kind) {
    case 'notified':
      return row.notice.at === null
        ? t('alerts.notice.notifiedNoTime')
        : t('alerts.notice.notified', { time: formatHhMm(row.notice.at) });
    case 'shadow':
      return t('alerts.notice.shadow');
    case 'foreign_origin':
      return t('alerts.notice.foreign_origin');
    case 'policy_skip':
      return t('alerts.notice.policy_skip');
    case 'none':
      return t('alerts.notice.none');
    default: {
      const exhaustive: never = row.notice.kind;
      return exhaustive;
    }
  }
}

/** Gate/terminal, joined, when the provider reported either — `notice.channel` is
 *  always null in this version and never rendered (see wave-B server facts). */
export function liveDetails(row: FlightAlertRow): string | null {
  const parts: string[] = [];
  if (row.live.departureTerminal) {
    parts.push(t('alerts.live.terminal', { terminal: row.live.departureTerminal }));
  }
  if (row.live.departureGate) {
    parts.push(t('alerts.live.gate', { gate: row.live.departureGate }));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
